import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import type { inferRouterInputs, inferRouterOutputs } from '@trpc/server';

import AdminLayout from '@/components/Layout/AdminLayout';
import { showToast } from '@/components/Layout/Toast';
import Alert from '@/components/UI/Alert';
import Button from '@/components/UI/Button';
import Card from '@/components/UI/Card';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner';
import FormInput from '@/components/UI/FormInput';
import FormSelect from '@/components/UI/FormSelect';
import { StatusBadge } from '@/components/UI';
import type { StatusTone } from '@/components/UI/StatusBadge';
import { trpc } from '@/api/trpc';
import { useAppQuery } from '@/hooks/useAppQuery';
import type { AppRouter } from '@/types/appRouter';

type RouterInputs = inferRouterInputs<AppRouter>;
type RouterOutputs = inferRouterOutputs<AppRouter>;
type ValidationRun = RouterOutputs['elliottWaveAudit']['listValidationRuns']['runs'][number];
type ValidationReport = RouterOutputs['elliottWaveAudit']['getValidationRun']['run']['report'];
type ReviewCandidate = ValidationReport['endingDiagonalReviewCandidates'][number];
type ReviewReason = ReviewCandidate['reasons'][number];
type ReviewVerdict = RouterInputs['elliottWaveAudit']['saveReview']['verdict'];
type ReviewSurface = RouterInputs['elliottWaveAudit']['saveReview']['reviewSurface'];
type ElliottWaveAuditReview = RouterOutputs['elliottWaveAudit']['saveReview']['review'];
type ElliottWaveAiReview = ValidationReport['endingDiagonalHistoricalHits'][number]['aiReview'];
type ElliottWaveAiReasonCode = NonNullable<ElliottWaveAiReview>['reasonCodes'][number];
type HistoricalHit = ValidationReport['endingDiagonalHistoricalHits'][number];
type ReviewBadgeTarget = {
    updatedAt?: string | null;
    verdict: ReviewVerdict;
};

type RunOption = {
    label: string;
    value: string;
};

const VERDICT_OPTIONS: {
    label: string;
    value: ReviewVerdict;
    activeClasses: string;
    inactiveClasses: string;
}[] = [
    {
        value: 'accept',
        label: 'Accept',
        activeClasses: 'border-green-500 bg-green-500/20 text-green-300 ring-1 ring-green-500/40',
        inactiveClasses: 'border-zinc-700 bg-zinc-900 text-zinc-400 hover:border-green-600 hover:text-green-400'
    },
    {
        value: 'reject',
        label: 'Reject',
        activeClasses: 'border-red-500 bg-red-500/20 text-red-300 ring-1 ring-red-500/40',
        inactiveClasses: 'border-zinc-700 bg-zinc-900 text-zinc-400 hover:border-red-600 hover:text-red-400'
    },
    {
        value: 'unsure',
        label: 'Unsure',
        activeClasses: 'border-zinc-400 bg-zinc-400/20 text-zinc-200 ring-1 ring-zinc-400/40',
        inactiveClasses: 'border-zinc-700 bg-zinc-900 text-zinc-400 hover:border-zinc-500 hover:text-zinc-300'
    }
];

const REVIEW_REASON_LABELS: Record<ReviewReason, string> = {
    parityMismatch: 'Live, backtest, and direct summary views disagree for this asset/timestamp.',
    missingSnapshot: 'The expected Elliott snapshot was missing for this asset/timestamp.',
    highDisagreement: 'Bullish and bearish scenarios are competing too closely.',
    actionableHighDisagreement: 'The setup is actionable, but disagreement is still elevated.',
    bullBearConflict: 'Bullish and bearish counts both look plausible right now.',
    multipleCurrentScenarios: 'More than one current scenario remains in play.',
    terminalRisk: 'The structure may already be late or exhausted.',
    actionableCounterHigherDegree: 'The local setup fights the larger-degree direction.',
    nonActivePrimary: 'The primary scenario is not in a clean active state.',
    zigzagPrimary: 'A zigzag correction is currently the primary interpretation.',
    zigzagAlternate: 'A zigzag correction is competing as an alternate interpretation.',
    flatPrimary: 'A flat correction is currently the primary interpretation.',
    flatAlternate: 'A flat correction is competing as an alternate interpretation.',
    endingDiagonalPrimary: 'An ending diagonal is currently the primary interpretation.',
    endingDiagonalAlternate: 'An ending diagonal is competing as an alternate interpretation.',
    leadingDiagonalPrimary: 'A leading diagonal is currently the primary interpretation.',
    leadingDiagonalAlternate: 'A leading diagonal is competing as an alternate interpretation.'
};

const AI_REASON_LABELS: Record<ElliottWaveAiReasonCode, string> = {
    anchorsArbitrary: 'The claimed swing anchors look arbitrary or forced.',
    noisyChop: 'The chart looks more like noisy consolidation than structure.',
    weakWedgeCompression: 'The wedge/compression shape is too weak.',
    weakABCStructure: 'The A-B-C corrective structure is too weak or unclear.',
    weakBWaveRelation: 'The B-wave retracement relationship does not look credible.',
    weakCWaveCompletion: 'The C-wave completion does not look convincing.',
    poorAlternation: 'The swings do not alternate cleanly enough.',
    wrongLifecycleContext: 'The setup appears in the wrong part of the broader move.',
    weakKickoffStructure: 'The leading-diagonal kickoff thesis looks too weak.',
    weakTerminalExhaustion: 'The ending-diagonal exhaustion thesis looks too weak.',
    insufficientTrendContext: 'The prior trend context is not strong enough.',
    invalidationNotCredible: 'The invalidation or target framing does not look believable.',
    other: 'The setup fails for another chart-structure reason.'
};

const extractRunGeneratedAt = (runId: string): string | null => {
    const matches = runId.match(/(\d{4})-(\d{2})-(\d{2})-(\d{2})-(\d{2})-(\d{2})$/);

    if (!matches) return null;

    const [, year, month, day, hour, minute] = matches;
    return `${year}-${month}-${day} ${hour}:${minute}`;
};

const formatRunLabel = (run: ValidationRun) => {
    const sourceLabel = run.args.preset ?? run.args.universe ?? run.runId;
    const generatedAt = extractRunGeneratedAt(run.runId);
    const historicalHitsLabel = `ED hits ${String(run.endingDiagonalHistoricalHitsCount)}`;
    const impulseReviewsLabel = `IMP reviews ${String(run.impulseConflictReviewCandidatesCount)}`;
    const zigzagReviewsLabel = `ZZ reviews ${String(run.zigzagReviewCandidatesCount)}`;
    const flatReviewsLabel = `Flat reviews ${String(run.flatReviewCandidatesCount)}`;
    const leadingReviewsLabel = `LD reviews ${String(run.leadingDiagonalReviewCandidatesCount)}`;

    return `${sourceLabel} · ${run.args.duration}/${run.args.degree} · ${run.args.endDate} · ${historicalHitsLabel} · ${impulseReviewsLabel} · ${zigzagReviewsLabel} · ${flatReviewsLabel} · ${leadingReviewsLabel}${generatedAt ? ` · ${generatedAt}` : ''}`;
};

const formatNumber = (value: number | null | undefined, fractionDigits = 2): string => {
    if (value === null || value === undefined || Number.isNaN(value)) return '—';
    return value.toFixed(fractionDigits);
};

const formatReviewUpdatedAt = (updatedAt: string | null | undefined): string => {
    if (!updatedAt) return '—';
    return updatedAt.replace('T', ' ').slice(0, 16);
};

const formatConfidence = (value: number | null | undefined): string => {
    if (value === null || value === undefined || Number.isNaN(value)) return '—';
    return `${Math.round(value * 100)}%`;
};

const verdictSummaryLabel = (review: ReviewBadgeTarget | null): string => {
    if (!review) return 'Unreviewed';
    if (review.verdict === 'accept') return 'Accepted';
    if (review.verdict === 'reject') return 'Rejected';
    return 'Unsure';
};

const verdictSummaryTone = (review: ReviewBadgeTarget | null): StatusTone => {
    if (review?.verdict === 'accept') return 'success';
    if (review?.verdict === 'reject') return 'danger';
    return 'neutral';
};

const buildReviewKey = (config: { assetId: number; scenarioKey: string; snapshotDateTime: string }): string =>
    `${String(config.assetId)}:${config.snapshotDateTime}:${config.scenarioKey}`;

type VerdictButtonsProps = {
    disabled?: boolean;
    isSaving: boolean;
    review: ElliottWaveAuditReview | null;
    onSaveVerdict: (verdict: ReviewVerdict) => Promise<void>;
};

const VerdictButtons = ({ disabled = false, isSaving, review, onSaveVerdict }: VerdictButtonsProps) => (
    <div className="space-y-2">
        <StatusBadge status={verdictSummaryLabel(review)} tone={verdictSummaryTone(review)}>
            {verdictSummaryLabel(review)}
            {review ? ` · ${formatReviewUpdatedAt(review.updatedAt)}` : ''}
        </StatusBadge>
        <div className="flex flex-wrap gap-2">
            {VERDICT_OPTIONS.map((option) => {
                const isActive = review?.verdict === option.value;

                return (
                    <button
                        className={`rounded border px-3 py-1.5 text-xs font-medium transition-all ${isActive ? option.activeClasses : option.inactiveClasses}`}
                        disabled={disabled || isSaving}
                        key={option.value}
                        type="button"
                        onClick={async () => {
                            await onSaveVerdict(option.value);
                        }}
                    >
                        {option.label}
                    </button>
                );
            })}
        </div>
    </div>
);

type AiReviewPanelProps = {
    aiReview: ElliottWaveAiReview | null;
};

const AiReviewPanel = ({ aiReview }: AiReviewPanelProps) => {
    if (!aiReview) {
        return <div className="text-xs text-zinc-500">No AI review yet.</div>;
    }

    return (
        <div className="space-y-2">
            <StatusBadge status={verdictSummaryLabel(aiReview)} tone={verdictSummaryTone(aiReview)}>
                AI {verdictSummaryLabel(aiReview)} · {formatConfidence(aiReview.confidence)} · {aiReview.reviewerModelKey}
            </StatusBadge>
            {aiReview.notes ? <div className="text-xs leading-5 text-zinc-300">{aiReview.notes}</div> : null}
            {aiReview.reasonCodes.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                    {aiReview.reasonCodes.map((reasonCode) => (
                        <span
                            className="rounded-full border border-zinc-700 bg-zinc-900 px-2 py-1 text-[11px] text-zinc-300"
                            key={reasonCode}
                            title={AI_REASON_LABELS[reasonCode]}
                        >
                            {reasonCode}
                        </span>
                    ))}
                </div>
            ) : null}
        </div>
    );
};

const pickDefaultRunId = (runs: ValidationRun[]): string | null => {
    const prioritizedRun =
        runs.find((run) => run.endingDiagonalHistoricalHitsCount > 0) ??
        runs.find((run) => run.impulseConflictReviewCandidatesCount > 0) ??
        runs.find((run) => run.zigzagReviewCandidatesCount > 0) ??
        runs.find((run) => run.flatReviewCandidatesCount > 0) ??
        runs.find((run) => run.endingDiagonalReviewCandidatesCount > 0) ??
        runs.find((run) => run.leadingDiagonalReviewCandidatesCount > 0) ??
        runs[0];

    return prioritizedRun?.runId ?? null;
};

const getReviewTargetMessage = (report: ValidationReport): string => {
    if (report.endingDiagonalHistoricalHits.length > 0) {
        return 'Start with the historical ending-diagonal hits. Those are the easiest manual QA cases because the detector has already frozen a specific snapshot for review.';
    }

    if (report.endingDiagonalReviewCandidates.length > 0) {
        return 'This run has no historical ending-diagonal hits, so review the current ending-diagonal candidates below.';
    }

    if (report.impulseConflictReviewCandidates.length > 0) {
        return 'This run has no historical ending-diagonal hits, so review the conflicted impulse candidates below.';
    }

    if (report.zigzagReviewCandidates.length > 0) {
        return 'This run has no historical ending-diagonal hits, so review the current zigzag candidates below.';
    }

    if (report.flatReviewCandidates.length > 0) {
        return 'This run has no ending-diagonal hits, so review the current flat candidates below.';
    }

    if (report.leadingDiagonalReviewCandidates.length > 0) {
        return 'This run has no historical ending-diagonal hits, so review the current leading-diagonal candidates below.';
    }

    return 'This run passed parity checks but does not currently offer a strong manual review queue. Pick another run with historical hits or diagonal review candidates.';
};

const getScenarioChecklist = (scenarioType: string | null | undefined): string[] => {
    if (scenarioType === 'endingDiagonal') {
        return [
            'Look for a wedge that appears late in an already mature move.',
            'The five pushes should overlap and feel compressed, not like a clean trend.',
            'The structure should look exhausted at the snapshot, not only after future bars print.'
        ];
    }

    if (scenarioType === 'leadingDiagonal') {
        return [
            'Look for a wedge that appears early in a new move or reversal attempt.',
            'The five pushes should still be directional, but they can overlap instead of separating cleanly.',
            'It should look like a credible kickoff move, not random chop.'
        ];
    }

    if (scenarioType === 'flat') {
        return [
            'Look for a believable three-part A-B-C correction rather than random range noise.',
            'The B-wave should retrace meaningfully without looking like an unrelated trend restart.',
            'The C-wave should look like a credible completion or exhaustion point at the snapshot.'
        ];
    }

    if (scenarioType === 'zigzag') {
        return [
            'Look for a clearer, more directional A-B-C correction than a flat, not sideways chop.',
            'The B-wave should look like a pause or retracement, not a full reset of the move.',
            'The C-wave should extend with conviction into a believable completion point.'
        ];
    }

    if (scenarioType === 'impulse') {
        return [
            'Look for a believable five-wave motive move rather than overlapping chop.',
            'Wave 3 should be the clearest directional thrust, not blend into noise.',
            'The motive count should still feel credible even with the competing alternate count.'
        ];
    }

    return [
        'Look for a clear, intentional structure rather than noisy chop.',
        'The anchor swings should make sense at the snapshot without using future bars.',
        'The setup should look tradable, with believable invalidation and target distances.'
    ];
};

const getVerdictGuide = (scenarioType: string | null | undefined): { accept: string; reject: string } => ({
    accept:
        scenarioType === 'endingDiagonal'
            ? 'Accept if you can clearly see a late-stage wedge and a believable reversal/exhaustion thesis at that timestamp.'
            : scenarioType === 'leadingDiagonal'
              ? 'Accept if you can clearly see an early wedge-like kickoff move that could reasonably launch a new leg.'
              : scenarioType === 'impulse'
                ? 'Accept if you can clearly see a directional five-wave motive move with a believable Wave 3 drive and tradable structure.'
                : scenarioType === 'zigzag'
                  ? 'Accept if you can clearly see a directional A-B-C correction with a believable C-wave completion at that timestamp.'
                  : 'Accept if the structure looks coherent and tradable at the snapshot.',
    reject: 'Reject if the anchors feel arbitrary, the chart looks like ordinary chop, or the thesis only becomes obvious after later price action.'
});

const formatReviewReasons = (reasons: ReviewReason[]): string[] => reasons.map((reason) => REVIEW_REASON_LABELS[reason]);

const ElliottWaveAudit = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [symbolFilter, setSymbolFilter] = useState('');
    const [pendingReviewKey, setPendingReviewKey] = useState<string | null>(null);

    const runsQuery = useAppQuery({
        ...trpc.elliottWaveAudit.listValidationRuns.queryOptions({ limit: 50 })
    });

    const runs = runsQuery.data?.runs ?? [];
    const preferredRunId = pickDefaultRunId(runs);
    const requestedRunId = searchParams.get('runId');
    const activeRunId = requestedRunId ?? preferredRunId;

    useEffect(() => {
        if (!preferredRunId) return;

        const hasRequestedRun = requestedRunId ? runs.some((run) => run.runId === requestedRunId) : false;

        if (!requestedRunId || !hasRequestedRun) {
            setSearchParams({ runId: preferredRunId }, { replace: true });
        }
    }, [preferredRunId, requestedRunId, runs, setSearchParams]);

    const runQuery = useAppQuery({
        ...trpc.elliottWaveAudit.getValidationRun.queryOptions(activeRunId ? { runId: activeRunId } : { runId: '' }),
        enabled: activeRunId !== null
    });

    const createChartLinkMutation = useMutation({
        ...trpc.elliottWaveAudit.createHistoricalHitChartLink.mutationOptions({
            onError: (error) => {
                showToast(error.message, 'error');
            }
        })
    });

    const saveReviewMutation = useMutation({
        ...trpc.elliottWaveAudit.saveReview.mutationOptions({
            onError: (error) => {
                showToast(error.message, 'error');
            },
            onSuccess: () => {
                showToast('Review saved', 'success');
            }
        })
    });

    const runAiReviewMutation = useMutation({
        ...trpc.elliottWaveAudit.runAiReview.mutationOptions({
            onError: (error) => {
                showToast(error.message, 'error');
            },
            onSuccess: async (data) => {
                const reviewedCount = data.result.reviewedTargets.length;
                const failedCount = data.result.failedTargets.length;
                const skippedCount = data.result.skippedTargets.length;

                if (failedCount > 0) {
                    showToast(`AI audit failed for ${failedCount} target(s)`, 'error');
                } else if (reviewedCount > 0) {
                    showToast(`AI audit saved ${reviewedCount} review(s)`, 'success');
                } else if (skippedCount > 0) {
                    showToast('AI audit skipped the requested targets', 'warning');
                } else {
                    showToast('No Elliott targets needed AI review', 'warning');
                }

                await runQuery.refetch();
            }
        })
    });

    const activeRun = runQuery.data?.run ?? null;
    const activeReport = activeRun?.report ?? null;

    const primaryReviewScenarioType =
        activeReport?.endingDiagonalHistoricalHits[0]?.scenarioType ??
        activeReport?.impulseConflictReviewCandidates[0]?.primary?.scenarioType ??
        activeReport?.zigzagReviewCandidates[0]?.primary?.scenarioType ??
        activeReport?.flatReviewCandidates[0]?.primary?.scenarioType ??
        activeReport?.endingDiagonalReviewCandidates[0]?.primary?.scenarioType ??
        activeReport?.leadingDiagonalReviewCandidates[0]?.primary?.scenarioType ??
        null;

    const runOptions = useMemo<RunOption[]>(
        () =>
            runs.map((run) => ({
                value: run.runId,
                label: formatRunLabel(run)
            })),
        [runs]
    );

    const filteredHistoricalHits = useMemo(() => {
        const historicalHits = activeReport?.endingDiagonalHistoricalHits ?? [];
        const normalizedFilter = symbolFilter.trim().toUpperCase();

        if (!normalizedFilter) return historicalHits;

        return historicalHits.filter(
            (hit) =>
                hit.symbol.toUpperCase().includes(normalizedFilter) ||
                hit.scenarioKey.toUpperCase().includes(normalizedFilter) ||
                hit.snapshotDateTime.toUpperCase().includes(normalizedFilter)
        );
    }, [activeReport?.endingDiagonalHistoricalHits, symbolFilter]);

    const handleRunChange = (runId: string) => {
        setSearchParams({ runId });
    };

    const handleRefreshRuns = async () => {
        await runsQuery.refetch();

        if (activeRunId) {
            await runQuery.refetch();
        }
    };

    const handleRunAiReview = async () => {
        if (!activeRunId) return;

        await runAiReviewMutation.mutateAsync({
            runId: activeRunId,
            overwrite: false
        });
    };

    const handleOpenHistoricalHitChart = async (config: { assetId: number; scenarioKey: string; snapshotDateTime: string }) => {
        if (!activeReport) return;

        const result = await createChartLinkMutation.mutateAsync({
            assetId: config.assetId,
            scenarioKey: config.scenarioKey,
            snapshotDateTime: config.snapshotDateTime,
            duration: activeReport.args.duration,
            degree: activeReport.args.degree
        });

        window.open(result.url, '_blank', 'noopener,noreferrer');
    };

    const handleOpenReviewCandidateChart = async (candidate: ReviewCandidate) => {
        if (!candidate.primary) return;

        await handleOpenHistoricalHitChart({
            assetId: candidate.assetId,
            scenarioKey: candidate.primary.scenarioKey,
            snapshotDateTime: candidate.asOfDateTime
        });
    };

    const handleSaveReview = async (config: {
        assetId: number;
        reviewSurface: ReviewSurface;
        scenarioKey: string;
        snapshotDateTime: string;
        verdict: ReviewVerdict;
    }) => {
        if (!activeRunId) return;

        const reviewKey = buildReviewKey(config);
        setPendingReviewKey(reviewKey);

        try {
            await saveReviewMutation.mutateAsync({
                runId: activeRunId,
                reviewSurface: config.reviewSurface,
                assetId: config.assetId,
                scenarioKey: config.scenarioKey,
                snapshotDateTime: config.snapshotDateTime,
                verdict: config.verdict,
                notes: null
            });

            await runQuery.refetch();
        } finally {
            setPendingReviewKey(null);
        }
    };

    const handleSaveHistoricalHitReview = async (hit: HistoricalHit, verdict: ReviewVerdict) => {
        await handleSaveReview({
            reviewSurface: hit.reviewSurface,
            assetId: hit.assetId,
            scenarioKey: hit.scenarioKey,
            snapshotDateTime: hit.snapshotDateTime,
            verdict
        });
    };

    const handleSaveCandidateReview = async (candidate: ReviewCandidate, verdict: ReviewVerdict) => {
        if (!candidate.primary) return;

        await handleSaveReview({
            reviewSurface: candidate.reviewSurface,
            assetId: candidate.assetId,
            scenarioKey: candidate.primary.scenarioKey,
            snapshotDateTime: candidate.asOfDateTime,
            verdict
        });
    };

    return (
        <AdminLayout title="Elliott Wave Audit">
            <div className="max-w-7xl mx-auto space-y-6">
                <Alert className="border border-zinc-800 bg-[#111111] text-zinc-100" variant="dark">
                    This tool is for detector QA, not trade execution. You do not need to know Elliott Wave deeply. Your job is simply to
                    decide whether the flagged chart structure looks like a coherent Elliott setup at that timestamp, or whether it looks
                    like noisy pattern overfitting.
                </Alert>

                <Card
                    responsivePadding
                    headerActions={
                        activeRunId ? (
                            <div className="flex flex-wrap gap-2">
                                <Button isLoading={runAiReviewMutation.isPending} size="sm" variant="primary" onClick={handleRunAiReview}>
                                    Run AI Audit
                                </Button>
                                <Button size="sm" variant="secondary" onClick={handleRefreshRuns}>
                                    Refresh Runs
                                </Button>
                            </div>
                        ) : undefined
                    }
                    title="Validation Run"
                >
                    {runsQuery.isLoading ? (
                        <DataLoadingSpinner text="Loading Elliott validation runs..." />
                    ) : runsQuery.error ? (
                        <Alert variant="danger">Failed to load Elliott validation runs: {runsQuery.error.message}</Alert>
                    ) : runs.length === 0 ? (
                        <div className="text-zinc-400">No Elliott validation runs were found under `temp/elliott-wave-validations`.</div>
                    ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-4 items-start">
                            <FormSelect
                                label="Run"
                                options={runOptions}
                                value={activeRunId ?? ''}
                                onChange={(event) => handleRunChange(event.target.value)}
                            />

                            {activeReport ? (
                                <div className="rounded-lg border border-zinc-800 bg-zinc-950 px-4 py-3 text-sm text-zinc-300">
                                    <div className="text-zinc-500 uppercase tracking-[0.16em] text-[11px] mb-2">Scope</div>
                                    <div>
                                        preset={activeReport.args.preset ?? 'none'} · universe={activeReport.args.universe ?? 'manual'}
                                    </div>
                                    <div>
                                        duration={activeReport.args.duration} · degree={activeReport.args.degree} · window=
                                        {activeReport.args.startDate} → {activeReport.args.endDate}
                                    </div>
                                    <div>acceptancePassed={String(activeReport.acceptance.passed)}</div>
                                </div>
                            ) : null}
                        </div>
                    )}
                </Card>

                {runQuery.isLoading ? (
                    <Card responsivePadding title="Run Detail">
                        <DataLoadingSpinner text="Loading Elliott validation report..." />
                    </Card>
                ) : null}

                {activeReport ? (
                    <>
                        <Card responsivePadding title="What To Do Next">
                            <div className="space-y-3 text-sm text-zinc-300">
                                <div>{getReviewTargetMessage(activeReport)}</div>
                                <div className="rounded-lg border border-zinc-800 bg-zinc-950 px-4 py-3">
                                    <div className="text-zinc-500 uppercase tracking-[0.16em] text-[11px] mb-2">Your Actual Task</div>
                                    <div>
                                        Open a flagged chart and answer one question: does this look like a believable structured setup, or
                                        not?
                                    </div>
                                    <div className="mt-2 text-zinc-400">
                                        Use `Accept` when the structure looks coherent, `Reject` when it looks overfit or messy, and
                                        `Unsure` when you cannot confidently tell.
                                    </div>
                                </div>
                            </div>
                        </Card>

                        <Card responsivePadding title="AI Audit">
                            <div className="space-y-4">
                                <div className="text-sm text-zinc-300">
                                    Run an automated Elliott audit for this validation run. The AI reviews the same wide and focused charts,
                                    applies the same structure-coherence test, and saves structured reject reasons that we can use to tune
                                    the detector.
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    <Button isLoading={runAiReviewMutation.isPending} variant="primary" onClick={handleRunAiReview}>
                                        Run AI Audit
                                    </Button>
                                    <div className="text-xs text-zinc-500 self-center">
                                        Reviews unreviewed targets only, using the latest enabled vision model.
                                    </div>
                                </div>

                                {activeReport.aiReviewSummary ? (
                                    <div className="grid grid-cols-1 xl:grid-cols-[0.8fr_1.2fr] gap-4">
                                        <div className="rounded-lg border border-zinc-800 bg-zinc-950 px-4 py-3 space-y-2 text-sm text-zinc-300">
                                            <div className="text-zinc-500 uppercase tracking-[0.16em] text-[11px]">Latest AI Summary</div>
                                            <div>model={activeReport.aiReviewSummary.reviewerModelKey ?? 'unknown'}</div>
                                            <div>lastReviewedAt={formatReviewUpdatedAt(activeReport.aiReviewSummary.lastReviewedAt)}</div>
                                            <div>
                                                accepted={activeReport.aiReviewSummary.acceptCount} · rejected=
                                                {activeReport.aiReviewSummary.rejectCount} · unsure=
                                                {activeReport.aiReviewSummary.unsureCount}
                                            </div>
                                        </div>

                                        <div className="rounded-lg border border-zinc-800 bg-zinc-950 px-4 py-3 space-y-3">
                                            <div>
                                                <div className="text-zinc-500 uppercase tracking-[0.16em] text-[11px] mb-2">
                                                    Dominant AI Reject Reasons
                                                </div>
                                                {activeReport.aiReviewSummary.dominantReasonCodes.length > 0 ? (
                                                    <div className="flex flex-wrap gap-2">
                                                        {activeReport.aiReviewSummary.dominantReasonCodes.map((reason) => (
                                                            <span
                                                                className="rounded-full border border-zinc-700 bg-zinc-900 px-2.5 py-1 text-xs text-zinc-300"
                                                                key={reason.code}
                                                                title={AI_REASON_LABELS[reason.code]}
                                                            >
                                                                {reason.code} · {reason.count}
                                                            </span>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <div className="text-sm text-zinc-400">No dominant reject reasons yet.</div>
                                                )}
                                            </div>

                                            <div>
                                                <div className="text-zinc-500 uppercase tracking-[0.16em] text-[11px] mb-2">
                                                    Detector Tuning Ideas
                                                </div>
                                                {activeReport.aiReviewSummary.recommendationSummary.length > 0 ? (
                                                    <div className="space-y-2 text-sm text-zinc-300">
                                                        {activeReport.aiReviewSummary.recommendationSummary.map((recommendation) => (
                                                            <div key={recommendation}>• {recommendation}</div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <div className="text-sm text-zinc-400">
                                                        No tuning recommendations yet. Run more AI audits or collect more rejects first.
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="rounded-lg border border-zinc-800 bg-zinc-950 px-4 py-3 text-sm text-zinc-400">
                                        No AI reviews have been saved for this run yet.
                                    </div>
                                )}
                            </div>
                        </Card>

                        <div className="grid grid-cols-2 xl:grid-cols-12 gap-3">
                            <Card responsivePadding title="Matched">
                                <div className="text-3xl font-semibold text-zinc-100">{activeReport.acceptance.matchedCount}</div>
                            </Card>
                            <Card responsivePadding title="Mismatched">
                                <div className="text-3xl font-semibold text-zinc-100">{activeReport.acceptance.mismatchedCount}</div>
                            </Card>
                            <Card responsivePadding title="Historical Hits">
                                <div className="text-3xl font-semibold text-zinc-100">
                                    {activeReport.endingDiagonalHistoricalHits.length}
                                </div>
                            </Card>
                            <Card responsivePadding title="Current ED Reviews">
                                <div className="text-3xl font-semibold text-zinc-100">
                                    {activeReport.endingDiagonalReviewCandidates.length}
                                </div>
                            </Card>
                            <Card responsivePadding title="Current IMP Reviews">
                                <div className="text-3xl font-semibold text-zinc-100">
                                    {activeReport.impulseConflictReviewCandidates.length}
                                </div>
                            </Card>
                            <Card responsivePadding title="Current ZZ Reviews">
                                <div className="text-3xl font-semibold text-zinc-100">{activeReport.zigzagReviewCandidates.length}</div>
                            </Card>
                            <Card responsivePadding title="Current Flat Reviews">
                                <div className="text-3xl font-semibold text-zinc-100">{activeReport.flatReviewCandidates.length}</div>
                            </Card>
                            <Card responsivePadding title="Current LD Reviews">
                                <div className="text-3xl font-semibold text-zinc-100">
                                    {activeReport.leadingDiagonalReviewCandidates.length}
                                </div>
                            </Card>
                            <Card responsivePadding title="Coverage">
                                <div className="text-3xl font-semibold text-zinc-100">
                                    {(activeReport.acceptance.snapshotCoverageRate * 100).toFixed(0)}%
                                </div>
                            </Card>
                            <Card responsivePadding title="Reviewed">
                                <div className="text-3xl font-semibold text-zinc-100">{activeReport.reviewStats.reviewedCount}</div>
                            </Card>
                            <Card responsivePadding title="Need Review">
                                <div className="text-3xl font-semibold text-zinc-100">{activeReport.reviewStats.unreviewedCount}</div>
                            </Card>
                            <Card responsivePadding title="AI Reviewed">
                                <div className="text-3xl font-semibold text-zinc-100">{activeReport.aiReviewStats.reviewedCount}</div>
                            </Card>
                            <Card responsivePadding title="AI Need Review">
                                <div className="text-3xl font-semibold text-zinc-100">{activeReport.aiReviewStats.unreviewedCount}</div>
                            </Card>
                        </div>

                        <Card
                            responsivePadding
                            headerActions={
                                <div className="min-w-[220px]">
                                    <FormInput
                                        noMargin
                                        placeholder="Filter by symbol or scenario"
                                        value={symbolFilter}
                                        onChange={(event) => setSymbolFilter(event.target.value)}
                                    />
                                </div>
                            }
                            subtitle="These are the short-lived historical completion events that deserve manual chart review."
                            title="Ending Diagonal Historical Hits"
                        >
                            {!filteredHistoricalHits.length ? (
                                <div className="text-zinc-400">No ending diagonal hits matched the current filter.</div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="min-w-full text-sm">
                                        <thead className="text-zinc-400 border-b border-zinc-800">
                                            <tr>
                                                <th className="py-2 pr-4 text-left">Symbol</th>
                                                <th className="py-2 pr-4 text-left">Snapshot</th>
                                                <th className="py-2 pr-4 text-left">Direction</th>
                                                <th className="py-2 pr-4 text-left">State</th>
                                                <th className="py-2 pr-4 text-left">Rank</th>
                                                <th className="py-2 pr-4 text-left">Primary</th>
                                                <th className="py-2 pr-4 text-left">Actionable</th>
                                                <th className="py-2 pr-4 text-left">Quality</th>
                                                <th className="py-2 pr-4 text-left">Tradeability</th>
                                                <th className="py-2 pr-4 text-left">Review</th>
                                                <th className="py-2 pr-4 text-left">AI Review</th>
                                                <th className="py-2 pr-4 text-left">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredHistoricalHits.map((hit) => (
                                                <tr
                                                    className="border-b border-zinc-900/80 align-top"
                                                    key={`${hit.scenarioKey}:${hit.snapshotDateTime}`}
                                                >
                                                    <td className="py-3 pr-4 font-medium text-zinc-100">{hit.symbol}</td>
                                                    <td className="py-3 pr-4 text-zinc-300">{hit.snapshotDateTime}</td>
                                                    <td className="py-3 pr-4 text-zinc-300">{hit.direction}</td>
                                                    <td className="py-3 pr-4 text-zinc-300">{hit.state}</td>
                                                    <td className="py-3 pr-4 text-zinc-300">{hit.rank}</td>
                                                    <td className="py-3 pr-4 text-zinc-300">{String(hit.isPrimary)}</td>
                                                    <td className="py-3 pr-4 text-zinc-300">{String(hit.isActionable)}</td>
                                                    <td className="py-3 pr-4 text-zinc-300">{formatNumber(hit.qualityScore)}</td>
                                                    <td className="py-3 pr-4 text-zinc-300">{formatNumber(hit.tradeabilityScore)}</td>
                                                    <td className="py-3 pr-4">
                                                        <VerdictButtons
                                                            isSaving={
                                                                pendingReviewKey ===
                                                                buildReviewKey({
                                                                    assetId: hit.assetId,
                                                                    scenarioKey: hit.scenarioKey,
                                                                    snapshotDateTime: hit.snapshotDateTime
                                                                })
                                                            }
                                                            review={hit.review}
                                                            onSaveVerdict={(verdict) => handleSaveHistoricalHitReview(hit, verdict)}
                                                        />
                                                    </td>
                                                    <td className="py-3 pr-4 min-w-[280px]">
                                                        <AiReviewPanel aiReview={hit.aiReview} />
                                                    </td>
                                                    <td className="py-3 pr-4">
                                                        <div className="flex flex-wrap gap-2">
                                                            <Button
                                                                isLoading={createChartLinkMutation.isPending}
                                                                size="sm"
                                                                variant="primary"
                                                                onClick={() =>
                                                                    handleOpenHistoricalHitChart({
                                                                        assetId: hit.assetId,
                                                                        scenarioKey: hit.scenarioKey,
                                                                        snapshotDateTime: hit.snapshotDateTime
                                                                    })
                                                                }
                                                            >
                                                                Open Chart
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                to={`/asset/${hit.assetId}?duration=${activeReport.args.duration}&endDateTime=${encodeURIComponent(hit.snapshotDateTime)}`}
                                                                variant="outline-dark"
                                                            >
                                                                Asset
                                                            </Button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </Card>

                        <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">
                            <Card responsivePadding title="Current Impulse Conflict Review Candidates">
                                {activeReport.impulseConflictReviewCandidates.length === 0 ? (
                                    <div className="text-zinc-400">None for the selected as-of date.</div>
                                ) : (
                                    <div className="space-y-3">
                                        {activeReport.impulseConflictReviewCandidates.map((candidate) => (
                                            <div
                                                className="rounded border border-zinc-800 bg-zinc-950 px-4 py-3"
                                                key={candidate.primary?.scenarioKey ?? candidate.symbol}
                                            >
                                                <div className="font-medium text-zinc-100">
                                                    {candidate.symbol} ({candidate.assetId})
                                                </div>
                                                <div className="text-sm text-zinc-400">asOf={candidate.asOfDateTime}</div>
                                                <div className="mt-3 space-y-2 text-sm text-zinc-300">
                                                    <div className="font-medium text-zinc-100">Why this needs review</div>
                                                    {formatReviewReasons(candidate.reasons).map((reason) => (
                                                        <div key={reason}>• {reason}</div>
                                                    ))}
                                                </div>
                                                {candidate.primary ? (
                                                    <>
                                                        <div className="mt-3 space-y-2 text-sm text-zinc-300">
                                                            <div className="font-medium text-zinc-100">What to look for</div>
                                                            {getScenarioChecklist(candidate.primary.scenarioType).map((item) => (
                                                                <div key={item}>• {item}</div>
                                                            ))}
                                                        </div>
                                                        <div className="mt-3 text-xs text-zinc-500">
                                                            primary={candidate.primary.scenarioType} · state={candidate.primary.state} ·
                                                            quality={formatNumber(candidate.primary.qualityScore)}
                                                        </div>
                                                        <div className="mt-3">
                                                            <VerdictButtons
                                                                isSaving={
                                                                    pendingReviewKey ===
                                                                    buildReviewKey({
                                                                        assetId: candidate.assetId,
                                                                        scenarioKey: candidate.primary.scenarioKey,
                                                                        snapshotDateTime: candidate.asOfDateTime
                                                                    })
                                                                }
                                                                review={candidate.review}
                                                                onSaveVerdict={(verdict) => handleSaveCandidateReview(candidate, verdict)}
                                                            />
                                                        </div>
                                                        <div className="mt-3">
                                                            <AiReviewPanel aiReview={candidate.aiReview} />
                                                        </div>
                                                        <div className="mt-3 flex flex-wrap gap-2">
                                                            <Button
                                                                size="sm"
                                                                variant="primary"
                                                                onClick={() => handleOpenReviewCandidateChart(candidate)}
                                                            >
                                                                Open Chart
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                to={`/asset/${candidate.assetId}?duration=${activeReport.args.duration}&endDateTime=${encodeURIComponent(candidate.asOfDateTime)}`}
                                                                variant="outline-dark"
                                                            >
                                                                Asset
                                                            </Button>
                                                        </div>
                                                    </>
                                                ) : null}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Card>

                            <Card responsivePadding title="Current Zigzag Review Candidates">
                                {activeReport.zigzagReviewCandidates.length === 0 ? (
                                    <div className="text-zinc-400">None for the selected as-of date.</div>
                                ) : (
                                    <div className="space-y-3">
                                        {activeReport.zigzagReviewCandidates.map((candidate) => (
                                            <div
                                                className="rounded border border-zinc-800 bg-zinc-950 px-4 py-3"
                                                key={candidate.primary?.scenarioKey ?? candidate.symbol}
                                            >
                                                <div className="font-medium text-zinc-100">
                                                    {candidate.symbol} ({candidate.assetId})
                                                </div>
                                                <div className="text-sm text-zinc-400">asOf={candidate.asOfDateTime}</div>
                                                <div className="mt-3 space-y-2 text-sm text-zinc-300">
                                                    <div className="font-medium text-zinc-100">Why this needs review</div>
                                                    {formatReviewReasons(candidate.reasons).map((reason) => (
                                                        <div key={reason}>• {reason}</div>
                                                    ))}
                                                </div>
                                                {candidate.primary ? (
                                                    <>
                                                        <div className="mt-3 space-y-2 text-sm text-zinc-300">
                                                            <div className="font-medium text-zinc-100">What to look for</div>
                                                            {getScenarioChecklist(candidate.primary.scenarioType).map((item) => (
                                                                <div key={item}>• {item}</div>
                                                            ))}
                                                        </div>
                                                        <div className="mt-3 text-xs text-zinc-500">
                                                            primary={candidate.primary.scenarioType} · state={candidate.primary.state} ·
                                                            quality={formatNumber(candidate.primary.qualityScore)}
                                                        </div>
                                                        <div className="mt-3">
                                                            <VerdictButtons
                                                                isSaving={
                                                                    pendingReviewKey ===
                                                                    buildReviewKey({
                                                                        assetId: candidate.assetId,
                                                                        scenarioKey: candidate.primary.scenarioKey,
                                                                        snapshotDateTime: candidate.asOfDateTime
                                                                    })
                                                                }
                                                                review={candidate.review}
                                                                onSaveVerdict={(verdict) => handleSaveCandidateReview(candidate, verdict)}
                                                            />
                                                        </div>
                                                        <div className="mt-3">
                                                            <AiReviewPanel aiReview={candidate.aiReview} />
                                                        </div>
                                                        <div className="mt-3 flex flex-wrap gap-2">
                                                            <Button
                                                                size="sm"
                                                                variant="primary"
                                                                onClick={() => handleOpenReviewCandidateChart(candidate)}
                                                            >
                                                                Open Chart
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                to={`/asset/${candidate.assetId}?duration=${activeReport.args.duration}&endDateTime=${encodeURIComponent(candidate.asOfDateTime)}`}
                                                                variant="outline-dark"
                                                            >
                                                                Asset
                                                            </Button>
                                                        </div>
                                                    </>
                                                ) : null}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Card>

                            <Card responsivePadding title="Current Flat Review Candidates">
                                {activeReport.flatReviewCandidates.length === 0 ? (
                                    <div className="text-zinc-400">None for the selected as-of date.</div>
                                ) : (
                                    <div className="space-y-3">
                                        {activeReport.flatReviewCandidates.map((candidate) => (
                                            <div
                                                className="rounded border border-zinc-800 bg-zinc-950 px-4 py-3"
                                                key={candidate.primary?.scenarioKey ?? candidate.symbol}
                                            >
                                                <div className="font-medium text-zinc-100">
                                                    {candidate.symbol} ({candidate.assetId})
                                                </div>
                                                <div className="text-sm text-zinc-400">asOf={candidate.asOfDateTime}</div>
                                                <div className="mt-3 space-y-2 text-sm text-zinc-300">
                                                    <div className="font-medium text-zinc-100">Why this needs review</div>
                                                    {formatReviewReasons(candidate.reasons).map((reason) => (
                                                        <div key={reason}>• {reason}</div>
                                                    ))}
                                                </div>
                                                {candidate.primary ? (
                                                    <>
                                                        <div className="mt-3 space-y-2 text-sm text-zinc-300">
                                                            <div className="font-medium text-zinc-100">What to look for</div>
                                                            {getScenarioChecklist(candidate.primary.scenarioType).map((item) => (
                                                                <div key={item}>• {item}</div>
                                                            ))}
                                                        </div>
                                                        <div className="mt-3 text-xs text-zinc-500">
                                                            primary={candidate.primary.scenarioType} · state={candidate.primary.state} ·
                                                            quality={formatNumber(candidate.primary.qualityScore)}
                                                        </div>
                                                        <div className="mt-3">
                                                            <VerdictButtons
                                                                isSaving={
                                                                    pendingReviewKey ===
                                                                    buildReviewKey({
                                                                        assetId: candidate.assetId,
                                                                        scenarioKey: candidate.primary.scenarioKey,
                                                                        snapshotDateTime: candidate.asOfDateTime
                                                                    })
                                                                }
                                                                review={candidate.review}
                                                                onSaveVerdict={(verdict) => handleSaveCandidateReview(candidate, verdict)}
                                                            />
                                                        </div>
                                                        <div className="mt-3">
                                                            <AiReviewPanel aiReview={candidate.aiReview} />
                                                        </div>
                                                        <div className="mt-3 flex flex-wrap gap-2">
                                                            <Button
                                                                size="sm"
                                                                variant="primary"
                                                                onClick={() => handleOpenReviewCandidateChart(candidate)}
                                                            >
                                                                Open Chart
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                to={`/asset/${candidate.assetId}?duration=${activeReport.args.duration}&endDateTime=${encodeURIComponent(candidate.asOfDateTime)}`}
                                                                variant="outline-dark"
                                                            >
                                                                Asset
                                                            </Button>
                                                        </div>
                                                    </>
                                                ) : null}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Card>

                            <Card responsivePadding title="Current Ending Diagonal Review Candidates">
                                {activeReport.endingDiagonalReviewCandidates.length === 0 ? (
                                    <div className="text-zinc-400">
                                        None for the selected as-of date. If you want easier manual QA, pick a run with `ED hits &gt; 0` in
                                        the selector above.
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {activeReport.endingDiagonalReviewCandidates.map((candidate) => (
                                            <div
                                                className="rounded border border-zinc-800 bg-zinc-950 px-4 py-3"
                                                key={candidate.primary?.scenarioKey ?? candidate.symbol}
                                            >
                                                <div className="font-medium text-zinc-100">
                                                    {candidate.symbol} ({candidate.assetId})
                                                </div>
                                                <div className="text-sm text-zinc-400">asOf={candidate.asOfDateTime}</div>
                                                <div className="mt-3 space-y-2 text-sm text-zinc-300">
                                                    <div className="font-medium text-zinc-100">Why this needs review</div>
                                                    {formatReviewReasons(candidate.reasons).map((reason) => (
                                                        <div key={reason}>• {reason}</div>
                                                    ))}
                                                </div>
                                                {candidate.primary ? (
                                                    <>
                                                        <div className="mt-3 space-y-2 text-sm text-zinc-300">
                                                            <div className="font-medium text-zinc-100">What to look for</div>
                                                            {getScenarioChecklist(candidate.primary.scenarioType).map((item) => (
                                                                <div key={item}>• {item}</div>
                                                            ))}
                                                        </div>
                                                        <div className="mt-3 text-xs text-zinc-500">
                                                            primary={candidate.primary.scenarioType} · state={candidate.primary.state} ·
                                                            quality=
                                                            {formatNumber(candidate.primary.qualityScore)}
                                                        </div>
                                                        <div className="mt-3">
                                                            <VerdictButtons
                                                                isSaving={
                                                                    pendingReviewKey ===
                                                                    buildReviewKey({
                                                                        assetId: candidate.assetId,
                                                                        scenarioKey: candidate.primary.scenarioKey,
                                                                        snapshotDateTime: candidate.asOfDateTime
                                                                    })
                                                                }
                                                                review={candidate.review}
                                                                onSaveVerdict={(verdict) => handleSaveCandidateReview(candidate, verdict)}
                                                            />
                                                        </div>
                                                        <div className="mt-3">
                                                            <AiReviewPanel aiReview={candidate.aiReview} />
                                                        </div>
                                                        <div className="mt-3 flex flex-wrap gap-2">
                                                            <Button
                                                                size="sm"
                                                                variant="primary"
                                                                onClick={() => handleOpenReviewCandidateChart(candidate)}
                                                            >
                                                                Open Chart
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                to={`/asset/${candidate.assetId}?duration=${activeReport.args.duration}&endDateTime=${encodeURIComponent(candidate.asOfDateTime)}`}
                                                                variant="outline-dark"
                                                            >
                                                                Asset
                                                            </Button>
                                                        </div>
                                                    </>
                                                ) : null}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Card>

                            <Card responsivePadding title="Current Leading Diagonal Review Candidates">
                                {activeReport.leadingDiagonalReviewCandidates.length === 0 ? (
                                    <div className="text-zinc-400">None for the selected as-of date.</div>
                                ) : (
                                    <div className="space-y-3">
                                        {activeReport.leadingDiagonalReviewCandidates.map((candidate) => (
                                            <div
                                                className="rounded border border-zinc-800 bg-zinc-950 px-4 py-3"
                                                key={candidate.primary?.scenarioKey ?? candidate.symbol}
                                            >
                                                <div className="font-medium text-zinc-100">
                                                    {candidate.symbol} ({candidate.assetId})
                                                </div>
                                                <div className="text-sm text-zinc-400">asOf={candidate.asOfDateTime}</div>
                                                <div className="mt-3 space-y-2 text-sm text-zinc-300">
                                                    <div className="font-medium text-zinc-100">Why this needs review</div>
                                                    {formatReviewReasons(candidate.reasons).map((reason) => (
                                                        <div key={reason}>• {reason}</div>
                                                    ))}
                                                </div>
                                                {candidate.primary ? (
                                                    <>
                                                        <div className="mt-3 space-y-2 text-sm text-zinc-300">
                                                            <div className="font-medium text-zinc-100">What to look for</div>
                                                            {getScenarioChecklist(candidate.primary.scenarioType).map((item) => (
                                                                <div key={item}>• {item}</div>
                                                            ))}
                                                        </div>
                                                        <div className="text-xs text-zinc-500 mt-3">
                                                            primary={candidate.primary.scenarioType} · state={candidate.primary.state} ·
                                                            quality={formatNumber(candidate.primary.qualityScore)}
                                                        </div>
                                                        <div className="mt-3">
                                                            <VerdictButtons
                                                                isSaving={
                                                                    pendingReviewKey ===
                                                                    buildReviewKey({
                                                                        assetId: candidate.assetId,
                                                                        scenarioKey: candidate.primary.scenarioKey,
                                                                        snapshotDateTime: candidate.asOfDateTime
                                                                    })
                                                                }
                                                                review={candidate.review}
                                                                onSaveVerdict={(verdict) => handleSaveCandidateReview(candidate, verdict)}
                                                            />
                                                        </div>
                                                        <div className="mt-3">
                                                            <AiReviewPanel aiReview={candidate.aiReview} />
                                                        </div>
                                                        <div className="mt-3 flex flex-wrap gap-2">
                                                            <Button
                                                                size="sm"
                                                                variant="primary"
                                                                onClick={() => handleOpenReviewCandidateChart(candidate)}
                                                            >
                                                                Open Chart
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                to={`/asset/${candidate.assetId}?duration=${activeReport.args.duration}&endDateTime=${encodeURIComponent(candidate.asOfDateTime)}`}
                                                                variant="outline-dark"
                                                            >
                                                                Asset
                                                            </Button>
                                                        </div>
                                                    </>
                                                ) : null}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Card>
                        </div>

                        <Card responsivePadding title="How To Review">
                            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 text-sm text-zinc-300">
                                <div className="space-y-2 rounded-lg border border-zinc-800 bg-zinc-950 px-4 py-3">
                                    <div className="font-medium text-zinc-100">Ask These 4 Questions</div>
                                    <div>1. Do the anchor swings look deliberate, or do they feel arbitrary?</div>
                                    <div>
                                        2. Does the price shape resemble a real structured correction or wedge instead of random chop?
                                    </div>
                                    <div>3. Does the setup look plausible at that timestamp without needing future bars?</div>
                                    <div>4. Would you believe the invalidation/target logic if you saw this live?</div>
                                </div>
                                <div className="space-y-2 rounded-lg border border-zinc-800 bg-zinc-950 px-4 py-3">
                                    <div className="font-medium text-zinc-100">Verdict Rule</div>
                                    <div>{getVerdictGuide(primaryReviewScenarioType).accept}</div>
                                    <div>{getVerdictGuide(primaryReviewScenarioType).reject}</div>
                                    <div>
                                        If a flagged case looks bad, send me the symbol and timestamp from this page and I can tune the
                                        detector against that exact example.
                                    </div>
                                </div>
                            </div>
                        </Card>
                    </>
                ) : null}

                {runQuery.error ? <Alert variant="danger">Failed to load Elliott validation run: {runQuery.error.message}</Alert> : null}

                {activeRunId && !activeReport && !runQuery.isLoading ? (
                    <Alert variant="warning">
                        No Elliott validation report could be loaded for run <span className="font-mono">{activeRunId}</span>.
                    </Alert>
                ) : null}
            </div>
        </AdminLayout>
    );
};

export default ElliottWaveAudit;
